<script>
// utils and composables
import { login } from '../../api/auth';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { SESSION_STORAGE_KEYS } from 'dashboard/constants/sessionStorage';
import SessionStorage from 'shared/helpers/sessionStorage';
import { useBranding } from 'shared/composables/useBranding';
import AnalyticsHelper from 'dashboard/helper/AnalyticsHelper';
import { SESSION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';

// components
import SimpleDivider from '../../components/Divider/SimpleDivider.vue';
import FormInput from '../../components/Form/Input.vue';
import GoogleOAuthButton from '../../components/GoogleOauth/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MfaVerification from 'dashboard/components/auth/MfaVerification.vue';
import Testimonials from '../auth/signup/components/Testimonials/Index.vue';
import SessionLimitOverlay from 'dashboard/components/auth/SessionLimitOverlay.vue';

const ERROR_MESSAGES = {
  'no-account-found': 'LOGIN.OAUTH.NO_ACCOUNT_FOUND',
  'business-account-only': 'LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY',
  'saml-authentication-failed': 'LOGIN.SAML.API.ERROR_MESSAGE',
  'saml-not-enabled': 'LOGIN.SAML.API.ERROR_MESSAGE',
};

const IMPERSONATION_URL_SEARCH_KEY = 'impersonation';
const USER_NOT_CONFIRMED_ERROR_CODE = 'user_not_confirmed';

export default {
  components: {
    FormInput,
    GoogleOAuthButton,
    Spinner,
    NextButton,
    SimpleDivider,
    MfaVerification,
    Testimonials,
    SessionLimitOverlay,
    Icon,
  },
  props: {
    ssoAuthToken: { type: String, default: '' },
    ssoAccountId: { type: String, default: '' },
    ssoConversationId: { type: String, default: '' },
    email: { type: String, default: '' },
    authError: { type: String, default: '' },
  },
  setup() {
    const { replaceInstallationName } = useBranding();
    return {
      replaceInstallationName,
      v$: useVuelidate(),
    };
  },
  data() {
    return {
      // We need to initialize the component with any
      // properties that will be used in it
      credentials: {
        email: '',
        password: '',
      },
      loginApi: {
        message: '',
        showLoading: false,
        hasErrored: false,
      },
      error: '',
      mfaRequired: false,
      mfaToken: null,
      sessionsLimitReached: false,
      limitedSessions: [],
    };
  },
  validations() {
    return {
      credentials: {
        password: {
          required,
        },
        email: {
          required,
          email,
        },
      },
    };
  },
  computed: {
    ...mapGetters({ globalConfig: 'globalConfig/get' }),
    allowedLoginMethods() {
      return window.EasyDeskConfig.allowedLoginMethods || ['email'];
    },
    showGoogleOAuth() {
      return (
        this.allowedLoginMethods.includes('google_oauth') &&
        Boolean(window.EasyDeskConfig.googleOAuthClientId)
      );
    },
    showSignupLink() {
      return window.EasyDeskConfig.signupEnabled === 'true';
    },
    showSamlLogin() {
      return this.allowedLoginMethods.includes('saml');
    },
    isAEasyDeskInstance() {
      return this.globalConfig.installationName === 'EasyDesk';
    },
  },
  created() {
    if (this.ssoAuthToken) {
      this.submitLogin();
    }
    if (this.authError) {
      const messageKey = ERROR_MESSAGES[this.authError] ?? 'LOGIN.API.UNAUTH';
      // Use a method to get the translated text to avoid dynamic key warning
      const translatedMessage = this.getTranslatedMessage(messageKey);
      useAlert(translatedMessage);
      // wait for idle state
      this.requestIdleCallbackPolyfill(() => {
        // Remove the error query param from the url
        const { query } = this.$route;
        this.$router.replace({ query: { ...query, error: undefined } });
      });
    }
  },
  methods: {
    getTranslatedMessage(key) {
      // Avoid dynamic key warning by handling each case explicitly
      switch (key) {
        case 'LOGIN.OAUTH.NO_ACCOUNT_FOUND':
          return this.$t('LOGIN.OAUTH.NO_ACCOUNT_FOUND');
        case 'LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY':
          return this.$t('LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY');
        case 'LOGIN.API.UNAUTH':
        default:
          return this.$t('LOGIN.API.UNAUTH');
      }
    },
    // TODO: Remove this when Safari gets wider support
    // Ref: https://caniuse.com/requestidlecallback
    //
    requestIdleCallbackPolyfill(callback) {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(callback);
      } else {
        // Fallback for safari
        // Using a delay of 0 allows the callback to be executed asynchronously
        // in the next available event loop iteration, similar to requestIdleCallback
        setTimeout(callback, 0);
      }
    },
    showAlertMessage(message) {
      // Reset loading, current selected agent
      this.loginApi.showLoading = false;
      this.loginApi.message = message;
      useAlert(this.loginApi.message);
    },
    handleImpersonation() {
      // Detects impersonation mode via URL and sets a session flag to prevent user settings changes during impersonation.
      const urlParams = new URLSearchParams(window.location.search);
      const impersonation = urlParams.get(IMPERSONATION_URL_SEARCH_KEY);
      if (impersonation) {
        SessionStorage.set(SESSION_STORAGE_KEYS.IMPERSONATION_USER, true);
      }
    },
    submitLogin() {
      this.loginApi.hasErrored = false;
      this.loginApi.showLoading = true;

      const credentials = {
        email: this.email
          ? decodeURIComponent(this.email)
          : this.credentials.email,
        password: this.credentials.password,
        sso_auth_token: this.ssoAuthToken,
        ssoAccountId: this.ssoAccountId,
        ssoConversationId: this.ssoConversationId,
      };

      login(credentials)
        .then(result => {
          // Check if MFA is required
          if (result?.mfaRequired) {
            this.loginApi.showLoading = false;
            this.mfaRequired = true;
            this.mfaToken = result.mfaToken;
            return;
          }

          // Check if sessions limit reached
          if (result?.sessionsLimitReached) {
            this.loginApi.showLoading = false;
            this.sessionsLimitReached = true;
            this.limitedSessions = result.sessions;
            AnalyticsHelper.track(SESSION_EVENTS.LIMIT_HIT);
            return;
          }

          this.handleImpersonation();
          this.showAlertMessage(this.$t('LOGIN.API.SUCCESS_MESSAGE'));
        })
        .catch(response => {
          if (response?.errorCode === USER_NOT_CONFIRMED_ERROR_CODE) {
            this.loginApi.showLoading = false;
            this.$router.push({
              name: 'auth_verify_email',
              state: { email: credentials.email },
            });
            return;
          }

          // Reset URL Params if the authentication is invalid
          if (this.email) {
            window.location = '/app/login';
          }
          this.loginApi.hasErrored = true;
          this.showAlertMessage(
            response?.message || this.$t('LOGIN.API.UNAUTH')
          );
        });
    },
    submitFormLogin() {
      if (this.v$.credentials.email.$invalid && !this.email) {
        this.showAlertMessage(this.$t('LOGIN.EMAIL.ERROR'));
        return;
      }

      this.submitLogin();
    },
    handleMfaVerified() {
      // MFA verification successful, continue with login
      this.handleImpersonation();
      window.location = '/app';
    },
    handleMfaCancel() {
      // User cancelled MFA, reset state
      this.mfaRequired = false;
      this.mfaToken = null;
      this.credentials.password = '';
    },
    retryLoginWithParams(extraParams) {
      const credentials = {
        email: this.email
          ? decodeURIComponent(this.email)
          : this.credentials.email,
        password: this.credentials.password,
        sso_auth_token: this.ssoAuthToken,
        ssoAccountId: this.ssoAccountId,
        ssoConversationId: this.ssoConversationId,
        ...extraParams,
      };

      this.sessionsLimitReached = false;
      this.limitedSessions = [];
      this.loginApi.showLoading = true;
      login(credentials)
        .then(result => {
          if (result?.sessionsLimitReached) {
            this.loginApi.showLoading = false;
            this.sessionsLimitReached = true;
            this.limitedSessions = result.sessions;
            AnalyticsHelper.track(SESSION_EVENTS.LIMIT_HIT);
            return;
          }
          this.handleImpersonation();
          this.showAlertMessage(this.$t('LOGIN.API.SUCCESS_MESSAGE'));
        })
        .catch(response => {
          this.loginApi.hasErrored = true;
          this.showAlertMessage(
            response?.message || this.$t('LOGIN.API.UNAUTH')
          );
        });
    },
    handleSessionRevoke(sessionId) {
      this.retryLoginWithParams({ revoke_session_id: sessionId });
    },
    handleSessionRevokeAll() {
      this.retryLoginWithParams({ revoke_all_sessions: true });
    },
    handleSessionLimitCancel() {
      this.sessionsLimitReached = false;
      this.limitedSessions = [];
      this.credentials.password = '';
    },
  },
};
</script>

<template>
  <main
    class="relative min-h-screen overflow-hidden bg-gradient-to-r from-n-amber-3 via-white to-white dark:from-n-background dark:via-n-solid-2 dark:to-n-solid-3"
  >
    <div
      class="pointer-events-none absolute -top-40 left-1/2 h-80 w-[36rem] -translate-x-1/2 rounded-full bg-n-amber-5/50 blur-[160px] dark:bg-n-brand/10"
    />
    <div
      class="pointer-events-none absolute bottom-[-14rem] right-[-6rem] h-96 w-[34rem] rounded-full bg-n-amber-7/40 blur-[180px] dark:bg-n-blue-5/20"
    />

    <div class="relative flex min-h-screen flex-col-reverse lg:flex-row">
      <Testimonials v-if="isAEasyDeskInstance" class="hidden flex-1 lg:flex" />

      <section
        class="relative flex flex-1 items-center justify-center px-6 py-16 sm:px-10 lg:px-20"
      >
        <div class="relative z-10 w-full max-w-lg">
          <div class="mb-12 space-y-8">
            <div class="flex items-center gap-3">
              <img
                :src="globalConfig.logo"
                :alt="globalConfig.installationName"
                class="block w-auto h-9 shrink-0 dark:hidden"
              />
              <img
                v-if="globalConfig.logoDark"
                :src="globalConfig.logoDark"
                :alt="globalConfig.installationName"
                class="hidden w-auto h-9 shrink-0 dark:block"
              />
            </div>

            <span
              class="inline-flex items-center gap-2 rounded-full bg-n-amber-4 px-4 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-n-amber-11 shadow-sm ring-1 ring-n-amber-5/60"
            >
              <span class="i-lucide-rocket h-3.5 w-3.5" />
              {{ $t('LOGIN.INTRO_BADGE') }}
            </span>

            <div class="space-y-4">
              <h1 class="text-4xl font-bold leading-tight text-n-slate-12">
                {{ replaceInstallationName($t('LOGIN.TITLE')) }}
              </h1>
              <p class="text-base text-n-slate-11">
                {{ replaceInstallationName($t('LOGIN.SUBTITLE')) }}
              </p>
              <div
                v-if="showSignupLink"
                class="flex flex-wrap items-center gap-3 text-sm text-n-slate-11"
              >
                <span>{{ $t('COMMON.OR') }}</span>
                <router-link
                  to="auth/signup"
                  class="inline-flex items-center gap-2 rounded-full bg-n-amber-9 px-4 py-2 font-medium text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-n-amber-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-amber-8 focus-visible:ring-offset-2"
                >
                  <span class="i-lucide-user-plus h-4 w-4" />
                  {{ $t('LOGIN.CREATE_NEW_ACCOUNT') }}
                </router-link>
              </div>
            </div>
          </div>

          <div
            class="relative mt-10 overflow-hidden rounded-3xl border border-n-amber-4 bg-white/95 p-8 shadow-[0_32px_96px_-48px_rgba(255,106,0,0.65)] backdrop-blur-xl ring-1 ring-n-amber-4/70 transition-all duration-300 dark:border-n-container/30 dark:bg-n-solid-2/90 dark:ring-n-container sm:p-10"
            :class="{ 'animate-wiggle': loginApi.hasErrored }"
          >
            <div
              class="pointer-events-none absolute inset-x-10 -top-24 h-32 rounded-full bg-n-amber-5/50 blur-3xl dark:bg-n-brand/5"
            />
            <div class="relative">
              <div v-if="sessionsLimitReached">
                <SessionLimitOverlay
                  :sessions="limitedSessions"
                  @revoke="handleSessionRevoke"
                  @revoke-all="handleSessionRevokeAll"
                  @cancel="handleSessionLimitCancel"
                />
              </div>
              <div v-else-if="mfaRequired">
                <MfaVerification
                  :mfa-token="mfaToken"
                  @verified="handleMfaVerified"
                  @cancel="handleMfaCancel"
                />
              </div>
              <div v-else>
                <div v-if="email" class="flex h-40 items-center justify-center">
                  <Spinner color-scheme="primary" size="" />
                </div>
                <div v-else class="space-y-6">
                  <div
                    v-if="showGoogleOAuth || showSamlLogin"
                    class="space-y-4"
                  >
                    <GoogleOAuthButton v-if="showGoogleOAuth" />
                    <router-link
                      v-if="showSamlLogin"
                      to="/app/login/sso"
                      class="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-n-amber-3 px-4 py-3 text-base font-medium text-n-amber-11 ring-1 ring-inset ring-n-amber-5 transition hover:-translate-y-0.5 hover:bg-n-amber-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-n-amber-8 focus-visible:ring-offset-2 dark:bg-n-solid-3 dark:text-n-slate-12 dark:ring-n-container dark:hover:bg-n-alpha-2"
                    >
                      <Icon
                        icon="i-lucide-lock-keyhole"
                        class="size-5 text-n-slate-11"
                      />
                      <span class="ml-2 text-base font-medium text-n-slate-12">
                        {{ $t('LOGIN.SAML.LABEL') }}
                      </span>
                    </router-link>
                    <SimpleDivider
                      :label="$t('COMMON.OR')"
                      class="uppercase tracking-[0.3em] text-n-amber-6"
                    />
                  </div>

                  <form class="space-y-6" @submit.prevent="submitFormLogin">
                    <FormInput
                      v-model="credentials.email"
                      name="email_address"
                      type="text"
                      data-testid="email_input"
                      :tabindex="1"
                      required
                      :label="$t('LOGIN.EMAIL.LABEL')"
                      :placeholder="$t('LOGIN.EMAIL.PLACEHOLDER')"
                      :has-error="v$.credentials.email.$error"
                      @input="v$.credentials.email.$touch"
                    />
                    <FormInput
                      v-model="credentials.password"
                      type="password"
                      name="password"
                      data-testid="password_input"
                      required
                      :tabindex="2"
                      :label="$t('LOGIN.PASSWORD.LABEL')"
                      :placeholder="$t('LOGIN.PASSWORD.PLACEHOLDER')"
                      :has-error="v$.credentials.password.$error"
                      @input="v$.credentials.password.$touch"
                    >
                      <p
                        v-if="!globalConfig.disableUserProfileUpdate"
                        class="text-right"
                      >
                        <router-link
                          to="auth/reset/password"
                          class="text-sm font-medium text-link"
                          tabindex="4"
                        >
                          {{ $t('LOGIN.FORGOT_PASSWORD') }}
                        </router-link>
                      </p>
                    </FormInput>
                    <NextButton
                      lg
                      type="submit"
                      data-testid="submit_button"
                      class="w-full"
                      amber
                      :tabindex="3"
                      :label="$t('LOGIN.SUBMIT')"
                      :disabled="loginApi.showLoading"
                      :is-loading="loginApi.showLoading"
                    />
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </main>
</template>
